import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { ShieldCheck, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CompleteProfile() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [dob, setDob] = useState('');
  const [jurisdiction, setJurisdiction] = useState('IN');
  const [parentEmail, setParentEmail] = useState('');
  const [terms, setTerms] = useState(false);
  const [privacy, setPrivacy] = useState(false);
  const [disclaimer, setDisclaimer] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user?.account_status === 'active') navigate('/dashboard', { replace: true });
    if (user?.account_status === 'pending_parent_approval') navigate('/pending-approval', { replace: true });
  }, [user, navigate]);

  const age = dob ? Math.floor((Date.now() - new Date(dob).getTime()) / (365.25 * 24 * 3600 * 1000)) : null;
  const isMinor = age !== null && age < 18;
  const needsParentEmail = isMinor;
  const canSubmit = dob && terms && privacy && disclaimer && (!needsParentEmail || parentEmail.includes('@'));

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/auth/complete-profile`, {
        date_of_birth: dob,
        jurisdiction,
        parent_email: parentEmail,
        terms_accepted: terms,
        privacy_accepted: privacy,
        cognitive_disclaimer_accepted: disclaimer
      }, { withCredentials: true });

      if (refreshUser) await refreshUser();

      if (res.data.status === 'pending_parent_approval') {
        toast.success('Consent request sent to your parent');
        navigate('/pending-approval', { replace: true });
      } else {
        toast.success('Profile complete — welcome!');
        navigate('/dashboard', { replace: true });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to complete profile');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-12" data-testid="complete-profile-page">
      <div className="container-swiss max-w-lg">
        <div className="border border-[#09090B] p-8">
          <ShieldCheck className="w-10 h-10 text-[#09090B] mb-4" />
          <h1 className="text-2xl font-black tracking-tighter mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Complete Your Profile
          </h1>
          <p className="text-sm text-zinc-600 mb-6">
            We need a few details before you start training. Your data is protected under DPDP, COPPA & GDPR.
          </p>

          <div className="space-y-4">
            <div>
              <label className="mono-data text-xs text-zinc-500 mb-1 block">Date of Birth</label>
              <input type="date" value={dob} onChange={e => setDob(e.target.value)} max={new Date().toISOString().slice(0, 10)}
                className="input-swiss text-sm w-full" data-testid="dob-input" />
              {age !== null && (
                <p className="mono-data text-xs text-zinc-500 mt-1">Age: {age}{isMinor && ' — parent consent required'}</p>
              )}
            </div>

            <div>
              <label className="mono-data text-xs text-zinc-500 mb-1 block">Jurisdiction</label>
              <select value={jurisdiction} onChange={e => setJurisdiction(e.target.value)}
                className="input-swiss text-sm w-full" data-testid="jurisdiction-select">
                <option value="IN">India</option>
                <option value="US">United States</option>
                <option value="EU">European Union</option>
                <option value="OTHER">Other</option>
              </select>
            </div>

            {needsParentEmail && (
              <div className="border border-[#09090B] bg-zinc-100 p-4">
                <div className="flex items-start gap-2 mb-3">
                  <AlertCircle className="w-4 h-4 text-[#09090B] mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-zinc-700">
                    Since you're under 18, a parent or legal guardian must approve your account.
                    Enter their email — we'll send a consent request.
                  </p>
                </div>
                <label className="mono-data text-xs text-zinc-500 mb-1 block">Parent/Guardian Email</label>
                <input type="email" value={parentEmail} onChange={e => setParentEmail(e.target.value)}
                  placeholder="parent@email.com" className="input-swiss text-sm w-full" data-testid="parent-email-input" />
              </div>
            )}

            <div className="space-y-2 pt-2">
              <label className="flex items-start gap-3 text-xs cursor-pointer">
                <input type="checkbox" checked={terms} onChange={e => setTerms(e.target.checked)}
                  className="mt-0.5" data-testid="consent-terms" />
                <span>I agree to the <Link to="/terms" target="_blank" className="text-[#09090B] underline">Terms of Service</Link></span>
              </label>
              <label className="flex items-start gap-3 text-xs cursor-pointer">
                <input type="checkbox" checked={privacy} onChange={e => setPrivacy(e.target.checked)}
                  className="mt-0.5" data-testid="consent-privacy" />
                <span>I acknowledge the <Link to="/privacy" target="_blank" className="text-[#09090B] underline">Privacy Policy</Link> and consent to processing of my personal data</span>
              </label>
              <label className="flex items-start gap-3 text-xs cursor-pointer">
                <input type="checkbox" checked={disclaimer} onChange={e => setDisclaimer(e.target.checked)}
                  className="mt-0.5" data-testid="consent-disclaimer" />
                <span>I understand the <strong>BrainMap Score is an educational tool, not a medical or diagnostic assessment</strong></span>
              </label>
            </div>

            <button onClick={submit} disabled={!canSubmit || submitting}
              className="btn-primary w-full py-3 text-sm disabled:opacity-40" data-testid="submit-profile-btn">
              {submitting ? 'Submitting…' : needsParentEmail ? 'Send Parent Consent Request' : 'Activate Account'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
